/**
 * <mb-envio>: plazo de entrega y coste de envío de la ficha de producto (snippets/mb-envio-ficha.liquid).
 *
 * - Fecha de entrega calculada en el navegador con el reloj de Europe/Madrid: días laborables = lunes a viernes
 *   menos los festivos del ajuste mb_festivos. Hora de corte, tránsito y tramos de envío vienen de los ajustes del tema
 *   (se leen de los atributos data-* que pinta el snippet).
 * - Coste de envío según el peso (peso de la variante x cantidad) y el ajuste mb_tramos_envio.
 * - Cuenta atrás real solo cuando faltan menos de 3 h para la hora de corte (se refresca cada minuto, sin aria-live).
 *
 * Eventos que escucha para saber el peso y la cantidad:
 *   - document 'mb:variante' (detail = { variante_id, peso_g, precio_cents, cantidad }): lo publican las fichas propias.
 *   - 'shopify:product:select' (Horizon/StandardEvents.productSelect) en la sección: al cambiar de variante lee el peso
 *     del <mb-envio> que viene en el HTML nuevo de la sección.
 *   - 'quantity-selector:update' (ThemeEvents.quantitySelectorUpdate) en la sección: cambio de cantidad.
 *
 * Las funciones puras (calcularEntrega, calcularEnvio...) se exportan para probarlas en Node
 * (el elemento solo se registra si existe `customElements`).
 */

const DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const MESES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];
const MS_DIA = 86_400_000;
const EVENTO_VARIANTE_HORIZON = 'shopify:product:select';
const EVENTO_CANTIDAD_HORIZON = 'quantity-selector:update';
const UMBRAL_CUENTA_ATRAS_MIN = 180;
const LIMITE_BUCLE = 400;

/* ------------------------------------------------------------------ */
/* Fechas (funciones puras)                                            */
/* ------------------------------------------------------------------ */

/**
 * Fecha y hora civiles de Madrid en un instante dado.
 * @param {Date} [instante]
 * @returns {{ y: number, m: number, d: number, h: number, min: number }}
 */
export function partesMadrid(instante = new Date()) {
  const formato = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Madrid',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
  /** @type {Record<string, string>} */
  const partes = {};
  for (const parte of formato.formatToParts(instante)) partes[parte.type] = parte.value;
  return { y: Number(partes.year), m: Number(partes.month), d: Number(partes.day), h: Number(partes.hour), min: Number(partes.minute) };
}

/** Fecha civil como Date a medianoche UTC (así no influye el horario de verano). */
const fechaCivil = (/** @type {number} */ y, /** @type {number} */ m, /** @type {number} */ d) => new Date(Date.UTC(y, m - 1, d));
const clave = (/** @type {Date} */ fecha) => fecha.toISOString().slice(0, 10);
const sumarDias = (/** @type {Date} */ fecha, /** @type {number} */ n) => new Date(fecha.getTime() + n * MS_DIA);

/**
 * Lee las fechas YYYY-MM-DD de un texto (una por línea; se ignora lo que no sea una fecha válida).
 * @param {string} [texto]
 * @returns {Set<string>}
 */
export function leerFestivos(texto = '') {
  const festivos = new Set();
  for (const [, y, m, d] of String(texto).matchAll(/(\d{4})-(\d{2})-(\d{2})/g)) {
    const fecha = fechaCivil(Number(y), Number(m), Number(d));
    if (clave(fecha) === `${y}-${m}-${d}`) festivos.add(`${y}-${m}-${d}`);
  }
  return festivos;
}

/**
 * @param {Date} fecha
 * @param {Set<string>} festivos
 */
export function esLaborable(fecha, festivos) {
  const diaSemana = fecha.getUTCDay();
  return diaSemana !== 0 && diaSemana !== 6 && !festivos.has(clave(fecha));
}

/**
 * Suma n días laborables (n = 0 devuelve la misma fecha).
 * @param {Date} fecha
 * @param {number} n
 * @param {Set<string>} festivos
 */
export function sumarLaborables(fecha, n, festivos) {
  let resultado = fecha;
  let pendientes = n;
  let vueltas = 0;
  while (pendientes > 0 && vueltas < LIMITE_BUCLE) {
    resultado = sumarDias(resultado, 1);
    vueltas += 1;
    if (esLaborable(resultado, festivos)) pendientes -= 1;
  }
  return resultado;
}

/**
 * Día de salida y rango de entrega.
 * Sale hoy si hoy es laborable y aún no ha pasado la hora de corte; si no, el siguiente día laborable.
 * En los tarros se suman `diasMontaje` días laborables al día de salida (0 por defecto).
 * @param {object} p
 * @param {{ y: number, m: number, d: number, h: number, min: number }} p.ahora Reloj de Madrid
 * @param {number} [p.horaCorte] Hora de corte (16 = 16:00, 16.5 = 16:30)
 * @param {number} [p.transitoMin] Días laborables mínimos de tránsito
 * @param {number} [p.transitoMax] Días laborables máximos de tránsito
 * @param {number} [p.diasMontaje]
 * @param {Set<string>} [p.festivos]
 */
export function calcularEntrega({ ahora, horaCorte = 16, transitoMin = 1, transitoMax = 3, diasMontaje = 0, festivos = new Set() }) {
  const hoy = fechaCivil(ahora.y, ahora.m, ahora.d);
  const minutosAhora = ahora.h * 60 + ahora.min;
  const minutosCorte = Math.round(horaCorte * 60);
  const antesDelCorte = esLaborable(hoy, festivos) && minutosAhora < minutosCorte;

  let salida = antesDelCorte ? hoy : sumarLaborables(hoy, 1, festivos);
  if (diasMontaje > 0) salida = sumarLaborables(salida, Math.floor(diasMontaje), festivos);

  const minimo = Math.max(0, Math.floor(transitoMin));
  const maximo = Math.max(minimo, Math.floor(transitoMax));
  return {
    antesDelCorte,
    hoy,
    salida,
    desde: sumarLaborables(salida, minimo, festivos),
    hasta: sumarLaborables(salida, maximo, festivos),
    minutosRestantes: antesDelCorte ? minutosCorte - minutosAhora : null,
  };
}

/**
 * "jueves 15" (o "jueves 15 de octubre").
 * @param {Date} fecha
 * @param {boolean} [conMes]
 */
export function formatearDia(fecha, conMes = false) {
  const base = `${DIAS[fecha.getUTCDay()]} ${fecha.getUTCDate()}`;
  return conMes ? `${base} de ${MESES[fecha.getUTCMonth()]}` : base;
}

/** 13 -> "13:00", 13.5 -> "13:30". */
export function formatearHora(hora) {
  const horas = Math.floor(hora);
  const minutos = Math.round((hora - horas) * 60);
  return `${horas}:${String(minutos).padStart(2, '0')}`;
}

/** "hoy", "mañana" o "el lunes 12". */
function cuando(/** @type {Date} */ fecha, /** @type {Date} */ hoy) {
  const diferencia = Math.round((fecha.getTime() - hoy.getTime()) / MS_DIA);
  if (diferencia === 0) return 'hoy';
  if (diferencia === 1) return 'mañana';
  return `el ${formatearDia(fecha)}`;
}

/**
 * Texto de la entrega como lista de trozos: cadenas normales y { b } para lo que va en negrita.
 * @param {ReturnType<typeof calcularEntrega>} r
 * @param {{ modo?: string, horaCorte?: number, verbo?: string }} [opciones]
 * @returns {Array<string | { b: string }>}
 */
export function componerEntrega(r, { modo = 'granel', horaCorte = 16, verbo = 'lo montamos' } = {}) {
  const mismoDia = clave(r.desde) === clave(r.hasta);
  const conMes = r.desde.getUTCMonth() !== r.hasta.getUTCMonth();
  const rango = mismoDia
    ? ['te llega el ', { b: formatearDia(r.desde) }]
    : ['te llega entre el ', { b: formatearDia(r.desde, conMes) }, ' y el ', { b: formatearDia(r.hasta, conMes) }];
  const pedir = r.antesDelCorte ? `Pídelo antes de las ${formatearHora(horaCorte)}` : 'Pídelo ahora';

  if (modo === 'tarro') {
    return [`${pedir}, ${verbo} y sale ${cuando(r.salida, r.hoy)}: `, ...rango];
  }
  return [`${pedir} y `, ...rango];
}

/** 134 -> "Quedan 2 h 14 min"; 45 -> "Quedan 45 min"; 120 -> "Quedan 2 h". */
export function formatearCuentaAtras(minutos) {
  const horas = Math.floor(minutos / 60);
  const resto = minutos % 60;
  if (horas === 0) return `Quedan ${resto} min`;
  if (resto === 0) return `Quedan ${horas} h`;
  return `Quedan ${horas} h ${resto} min`;
}

/* ------------------------------------------------------------------ */
/* Envío por peso (funciones puras)                                    */
/* ------------------------------------------------------------------ */

/**
 * "2:5,95|5:6,95|9999:18,95" -> [{ kg: 2, precio: 5.95 }, ...] ordenado por kg.
 * @param {string} [texto]
 * @returns {Array<{ kg: number, precio: number }>}
 */
export function leerTramos(texto = '') {
  const tramos = [];
  for (const trozo of String(texto).split('|')) {
    const [kgTexto, precioTexto] = trozo.split(':');
    const kg = Number(String(kgTexto ?? '').trim().replace(',', '.'));
    const precio = Number(String(precioTexto ?? '').trim().replace(',', '.'));
    if (kgTexto?.trim() && precioTexto?.trim() && Number.isFinite(kg) && Number.isFinite(precio) && kg > 0 && precio >= 0) {
      tramos.push({ kg, precio });
    }
  }
  return tramos.sort((a, b) => a.kg - b.kg);
}

/**
 * Tramo de envío para un peso. `cabenG` son los gramos que aún caben en el mismo tramo
 * (0 si no hay peso, si es el último tramo o si no queda margen).
 * @param {object} p
 * @param {number} p.pesoG Peso de una unidad en gramos
 * @param {number} p.cantidad
 * @param {Array<{ kg: number, precio: number }>} p.tramos
 */
export function calcularEnvio({ pesoG, cantidad, tramos }) {
  const totalG = Math.max(0, Math.round(pesoG)) * Math.max(1, Math.round(cantidad));
  if (tramos.length === 0) return { totalG, tramo: null, minimo: null, cabenG: 0 };
  const minimo = tramos[0].precio;
  if (totalG <= 0) return { totalG, tramo: null, minimo, cabenG: 0 };

  let indice = tramos.findIndex((t) => totalG <= Math.round(t.kg * 1000));
  if (indice === -1) indice = tramos.length - 1;
  const esUltimo = indice === tramos.length - 1;
  const cabenG = esUltimo ? 0 : Math.max(0, Math.round(tramos[indice].kg * 1000) - totalG);
  return { totalG, tramo: tramos[indice], minimo, cabenG };
}

const EUROS = new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' });
const KILOS = new Intl.NumberFormat('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 1 });

/** 5.95 -> "5,95 €". */
export const formatearEuros = (/** @type {number} */ importe) => EUROS.format(importe);

/** Gramos que caben -> "1,4 kg" (redondeado hacia abajo) o "450 g"; cadena vacía si no llega a 10 g. */
export function formatearCaben(cabenG) {
  if (cabenG >= 1000) return `${KILOS.format(Math.floor(cabenG / 100) / 10)} kg`;
  const gramos = Math.floor(cabenG / 10) * 10;
  return gramos > 0 ? `${gramos} g` : '';
}

/**
 * Líneas de coste de envío.
 * @param {ReturnType<typeof calcularEnvio>} envio
 * @param {string} modo
 * @returns {{ coste: string, extra: string }}
 */
export function componerEnvio(envio, modo) {
  if (!envio.minimo && envio.minimo !== 0) return { coste: '', extra: '' };
  if (!envio.tramo) return { coste: `Envío desde ${formatearEuros(envio.minimo)} a península`, extra: '' };
  const caben = modo === 'granel' ? formatearCaben(envio.cabenG) : '';
  return {
    coste: `Envío ${formatearEuros(envio.tramo.precio)} a península`,
    extra: caben ? `Te caben ${caben} más con el mismo envío` : '',
  };
}

/* ------------------------------------------------------------------ */
/* Elemento                                                            */
/* ------------------------------------------------------------------ */

/** @param {unknown} valor */
function numero(valor) {
  if (valor === undefined || valor === null || valor === '') return undefined;
  const n = Number(valor);
  return Number.isFinite(n) ? n : undefined;
}

const Base = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;

class MbEnvio extends Base {
  #pesoG = 0;
  #cantidad = 1;
  #temporizador = 0;
  #ultimaEntrega = '';
  #controlador = new AbortController();

  connectedCallback() {
    this.#controlador = new AbortController();
    const { signal } = this.#controlador;
    const seccion = this.closest('.shopify-section') ?? document;

    this.#pesoG = numero(this.dataset.pesoG) ?? 0;
    this.#cantidad = this.#leerCantidadInicial(seccion);

    document.addEventListener('mb:variante', (e) => this.#alVariante(/** @type {CustomEvent} */ (e)), { signal });
    seccion.addEventListener(EVENTO_VARIANTE_HORIZON, (e) => this.#alCambiarVarianteHorizon(e), { signal });
    seccion.addEventListener(EVENTO_CANTIDAD_HORIZON, (e) => this.#alCambiarCantidad(/** @type {CustomEvent} */ (e)), { signal });
    document.addEventListener('visibilitychange', () => document.visibilityState === 'visible' && this.#pintarEntrega(), { signal });

    this.#pintarEntrega();
    this.#pintarCoste();
    this.#programar();
    // Los anuncios para lectores de pantalla empiezan después del primer pintado
    requestAnimationFrame(() => this.querySelector('[data-coste-grupo]')?.setAttribute('aria-live', 'polite'));
  }

  disconnectedCallback() {
    this.#controlador.abort();
    clearTimeout(this.#temporizador);
  }

  /** @param {ParentNode} seccion */
  #leerCantidadInicial(seccion) {
    const entrada = /** @type {HTMLInputElement | null} */ (seccion.querySelector('input[name="quantity"]'));
    return Math.max(1, Math.round(numero(entrada?.value) ?? 1));
  }

  #configuracion() {
    return {
      modo: this.dataset.modo === 'tarro' ? 'tarro' : 'granel',
      horaCorte: numero(this.dataset.horaCorte) ?? 16,
      transitoMin: numero(this.dataset.transitoMin) ?? 1,
      transitoMax: numero(this.dataset.transitoMax) ?? 3,
      diasMontaje: this.dataset.modo === 'tarro' ? (numero(this.dataset.diasMontaje) ?? 0) : 0,
      verbo: this.dataset.verbo || 'lo montamos',
    };
  }

  /** @param {CustomEvent} evento */
  #alVariante(evento) {
    const detalle = evento.detail ?? {};
    const peso = numero(detalle.peso_g);
    const cantidad = numero(detalle.cantidad);
    if (peso !== undefined) this.#pesoG = Math.max(0, peso);
    if (cantidad !== undefined) this.#cantidad = Math.max(1, Math.round(cantidad));
    this.#pintarCoste();
  }

  /** @param {Event} evento */
  #alCambiarVarianteHorizon(evento) {
    if (evento.target instanceof Element && evento.target.closest('product-card')) return;
    const promesa = /** @type {{ promise?: Promise<any> }} */ (evento).promise;
    if (!promesa?.then) return;

    promesa
      .then(({ detail }) => {
        const producto = this.dataset.producto;
        const nuevo = producto ? detail?.html?.querySelector(`mb-envio[data-producto="${producto}"]`) : null;
        const peso = numero(nuevo?.dataset.pesoG);
        if (peso === undefined) return;
        this.#pesoG = peso;
        this.#pintarCoste();
      })
      .catch(() => {});
  }

  /** @param {CustomEvent} evento */
  #alCambiarCantidad(evento) {
    // Con cartLine es una línea del carrito, no la cantidad de la ficha
    if (evento.detail?.cartLine) return;
    const cantidad = numero(evento.detail?.quantity);
    if (cantidad === undefined) return;
    this.#cantidad = Math.max(1, Math.round(cantidad));
    this.#pintarCoste();
  }

  #pintarEntrega() {
    const config = this.#configuracion();
    const entrega = this.querySelector('[data-entrega]');
    if (!entrega) return;

    const festivos = leerFestivos(this.dataset.festivos);
    const resultado = calcularEntrega({ ahora: partesMadrid(), festivos, ...config });
    const trozos = componerEntrega(resultado, config);

    const firma = JSON.stringify(trozos);
    if (firma !== this.#ultimaEntrega) {
      this.#ultimaEntrega = firma;
      entrega.replaceChildren(
        ...trozos.map((trozo) => {
          if (typeof trozo === 'string') return document.createTextNode(trozo);
          const negrita = document.createElement('strong');
          negrita.textContent = trozo.b;
          return negrita;
        })
      );
    }

    const cuenta = /** @type {HTMLElement | null} */ (this.querySelector('[data-cuenta]'));
    if (!cuenta) return;
    const faltan = resultado.minutosRestantes;
    if (faltan !== null && faltan > 0 && faltan < UMBRAL_CUENTA_ATRAS_MIN) {
      const texto = formatearCuentaAtras(faltan);
      if (cuenta.textContent !== texto) cuenta.textContent = texto;
      cuenta.hidden = false;
    } else {
      cuenta.hidden = true;
    }
  }

  #pintarCoste() {
    const tramos = leerTramos(this.dataset.tramos);
    if (tramos.length === 0) return; // se queda el texto de reserva del servidor
    const envio = calcularEnvio({ pesoG: this.#pesoG, cantidad: this.#cantidad, tramos });
    const { coste, extra } = componerEnvio(envio, this.#configuracion().modo);

    const lineaCoste = /** @type {HTMLElement | null} */ (this.querySelector('[data-coste]'));
    const lineaExtra = /** @type {HTMLElement | null} */ (this.querySelector('[data-extra]'));
    if (lineaCoste) {
      if (lineaCoste.textContent !== coste) lineaCoste.textContent = coste;
      lineaCoste.hidden = !coste;
    }
    if (lineaExtra) {
      if (lineaExtra.textContent !== extra) lineaExtra.textContent = extra;
      lineaExtra.hidden = !extra;
    }
  }

  /** Vuelve a calcular al empezar cada minuto (cuenta atrás, hora de corte, cambio de día). */
  #programar() {
    clearTimeout(this.#temporizador);
    const espera = 60_000 - (Date.now() % 60_000) + 100;
    this.#temporizador = window.setTimeout(() => {
      this.#pintarEntrega();
      this.#programar();
    }, espera);
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('mb-envio')) {
  customElements.define('mb-envio', MbEnvio);
}
